import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Download, Search, X } from "lucide-react";

import {
	useDecidePricingApprovalMutation,
	useListPricingApprovalsQuery,
} from "../api/pricingApprovals";

import {
	APPROVALS,
	type Approval,
	type ApprovalStatus,
	type ApprovalStepKind,
} from "./approvalsDemo";
import { toApproval } from "./approvalsLive";

type Tab = ApprovalStatus | "all";
type Sort = "oldest" | "newest" | "impact";

const TABS: { id: Tab; label: string }[] = [
	{ id: "pending", label: "Pending" },
	{ id: "approved", label: "Approved" },
	{ id: "sent_back", label: "Sent back" },
	{ id: "rejected", label: "Rejected" },
	{ id: "all", label: "All" },
];

// v9 .pill-warning / -success / -neutral / -error
const STATUS: Record<ApprovalStatus, { label: string; style: string }> = {
	pending: {
		label: "Pending",
		style: "bg-sand-100 text-sand-900 border-sand-300",
	},
	approved: {
		label: "Approved",
		style: "bg-green-100 text-green-800 border-green-200",
	},
	sent_back: {
		label: "Sent back",
		style: "bg-gray-100 text-gray-600 border-gray-200",
	},
	rejected: {
		label: "Rejected",
		style: "bg-red-50 text-red-700 border-red-100",
	},
};

const PREVIEW_STATE: Record<ApprovalStatus, string> = {
	pending: "Preview · not yet approved",
	approved: "Approved · ready to release",
	sent_back: "Sent back · the quote changes first",
	rejected: "Rejected",
};

// v9 case-chain dots (.ctl li.ev-*); #a2e63c and #1a3a7a are v9 constants with no theme token.
const DOT: Record<ApprovalStepKind | "none", string> = {
	email: "border-blue-500 bg-blue-500",
	ok: "border-[#a2e63c] bg-[#a2e63c]",
	mail: "border-[#1a3a7a] bg-white",
	err: "border-red-500 bg-red-500",
	none: "border-gray-300 bg-white",
};

const money = (n: number) =>
	`$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const age = (h: number) => (h < 24 ? `${h} h` : `${Math.floor(h / 24)} d`);
const savings = (a: Approval) => (a.currentFees ?? 0) - (a.proposedFees ?? 0);
// Live requests (ROC-87) carry no fee figures yet.
const fee = (n: number | undefined) => (n == null ? "—" : money(n));

const exportCsv = (rows: Approval[]) => {
	const header =
		"Id,Merchant,Type,Requested by,Proposed,Floor,Vs floor,Age (h),Status";
	const lines = rows.map((a) =>
		[
			a.id,
			a.merchant,
			a.type,
			a.requestedBy,
			a.proposed ?? "restricted",
			a.floor ?? "restricted",
			a.vsFloor ?? "",
			a.ageHours,
			STATUS[a.status].label,
		]
			.map((v) => `"${String(v).replace(/"/g, '""')}"`)
			.join(","),
	);
	const url = URL.createObjectURL(
		new Blob([[header, ...lines].join("\n")], { type: "text/csv" }),
	);
	const link = document.createElement("a");
	link.href = url;
	link.download = "approval-queue.csv";
	link.click();
	URL.revokeObjectURL(url);
};

const pill =
	"inline-flex items-center whitespace-nowrap rounded-full border px-2 py-1 text-xs font-medium leading-[1.3]";

const StatusBadge: React.FC<{ status: ApprovalStatus }> = ({ status }) => (
	<span className={`${pill} ${STATUS[status].style}`}>
		{STATUS[status].label}
	</span>
);

const actionBtn =
	"inline-flex min-h-[44px] items-center justify-center gap-2 whitespace-nowrap rounded-lg border px-4 py-2 text-sm font-medium leading-5 transition-colors";
const focusRing = "focus:border-primary-500 focus:ring-1 focus:ring-primary-500";
const filterSelect = `h-9 rounded-lg border border-gray-300 bg-white py-1 pl-2.5 pr-10 text-[13px] text-gray-900 ${focusRing}`;

const emailHead = (item: Approval): [string, string][] => {
	if (item.status === "pending")
		return [
			["To", item.approver],
			["From", "Basys Pricing <no-reply@basys.rocketfee.com>"],
			[
				"Subject",
				`Approval needed · ${item.merchant} · ${item.reason.split(" below")[0].split(" outside")[0]}`,
			],
		];
	if (item.status === "approved")
		return [
			["To", `${item.requestedBy} · rep`],
			["Subject", `Approved · ${item.merchant} · proposal attached`],
		];
	return [
		["To", item.requestedBy],
		["Subject", `${STATUS[item.status].label} · ${item.merchant}`],
	];
};

const Email: React.FC<{ item: Approval }> = ({ item }) => {
	const type = item.type.toLowerCase();
	const note = item.decisionNote ? `: "${item.decisionNote}"` : "";
	const merchant = <b className="font-medium">{item.merchant}</b>;
	return (
		<div className="mt-2 overflow-hidden rounded-lg border border-gray-200 bg-white">
			<div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 border-b border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-600">
				{emailHead(item).map(([k, v]) => (
					<React.Fragment key={k}>
						<b className="font-medium text-gray-500">{k}</b>
						<span>{v}</span>
					</React.Fragment>
				))}
			</div>
			<div className="p-4 text-sm leading-[1.6] text-gray-800">
				{item.status === "pending" ? (
					<>
						Hi {item.approver.split(" ")[0]},
						<br />
						<br />
						{item.requestedBy} submitted a {type} for {merchant} that is outside
						the limits:
						<br />• {item.reason}
						{item.currentFees != null && item.proposedFees != null && (
							<>
								<br />• Merchant saves {money(savings(item))} / month
							</>
						)}
						<br />
						<br />
						<span className="mt-3 inline-block rounded-lg bg-primary-500 px-4 py-2 font-medium text-white">
							Review in the approval queue
						</span>
						<br />
						<br />
						<span className="text-xs text-gray-500">
							Reminder after 10 min, then hourly.
						</span>
					</>
				) : item.status === "approved" ? (
					<>
						{item.approver} approved the {type} for {merchant}
						{note}.
						<br />
						<br />
						The branded proposal is attached and the Salesforce opportunity is
						updated.
					</>
				) : (
					<>
						{item.approver}{" "}
						{item.status === "rejected" ? "rejected" : "sent back"} the {type}{" "}
						for {merchant}
						{note}.
						<br />
						<br />
						{item.status === "rejected"
							? "The quote stays at the template limits."
							: "Edit the quote and resubmit. The merchant analysis is unchanged."}
					</>
				)}
			</div>
		</div>
	);
};

// v9 .ctl: a vertical line joins the dots, except after the last one.
const chainLine =
	"[&:not(:last-child)]:before:absolute [&:not(:last-child)]:before:-bottom-0.5 [&:not(:last-child)]:before:left-[5px] [&:not(:last-child)]:before:top-3.5 [&:not(:last-child)]:before:border-l-2 [&:not(:last-child)]:before:border-gray-200 [&:not(:last-child)]:before:content-['']";

const ReviewPane: React.FC<{
	item: Approval;
	onClose: () => void;
	onDecide: (status: ApprovalStatus, note: string) => void;
}> = ({ item, onClose, onDecide }) => {
	const [note, setNote] = useState("");
	const closeRef = useRef<HTMLButtonElement>(null);
	const saved = savings(item);
	useEffect(() => closeRef.current?.focus(), []);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	const figures = [
		["Proposed", item.proposed ?? "restricted"],
		["Floor", item.floor ?? "restricted"],
		["Vs floor", item.vsFloor ?? "—"],
	] as const;
	const fees = [
		["Current monthly fees", fee(item.currentFees)],
		["Proposed monthly fees", fee(item.proposedFees)],
	] as const;

	return (
		<div
			role="dialog"
			aria-modal="false"
			aria-labelledby="apPaneTitle"
			tabIndex={-1}
			className="fixed bottom-0 right-0 top-16 z-[120] flex w-[520px] max-w-full flex-col border-l border-gray-200 bg-white shadow-[-12px_0_40px_rgba(7,26,64,.18)]"
		>
			<div className="flex items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4">
				<div className="min-w-0">
					<div
						id="apPaneTitle"
						className="text-lg font-semibold text-gray-900 [overflow-wrap:anywhere]"
					>
						{item.merchant}
					</div>
					<div className="mt-1 flex flex-wrap items-center gap-1.5 text-[13px] text-gray-600">
						<span>
							{item.type} · {item.id} · requested by {item.requestedBy}
						</span>
						<StatusBadge status={item.status} />
					</div>
				</div>
				<button
					ref={closeRef}
					type="button"
					onClick={onClose}
					aria-label="Close the review pane"
					className="flex h-11 w-11 flex-none items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-200 hover:text-gray-700"
				>
					<X className="h-5 w-5" />
				</button>
			</div>
			<div className="flex-1 overflow-auto p-5">
				<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
					{figures.map(([label, value]) => (
						<div key={label} className="rounded-[10px] border border-gray-200 p-3">
							<div className="text-xs text-gray-500">{label}</div>
							<div
								className={`mt-0.5 text-xl font-semibold ${
									label === "Vs floor" && item.belowFloor && item.vsFloor
										? "text-red-700"
										: "text-gray-900"
								}`}
							>
								{value}
							</div>
							{label === "Vs floor" && item.note && (
								<div className="mt-0.5 text-[11px] text-gray-500">
									inside the floor, outside the rep range
								</div>
							)}
						</div>
					))}
				</div>
				<dl className="mt-4 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
					<dt className="text-gray-500">Reason</dt>
					<dd className="text-gray-900">{item.reason}</dd>
					<dt className="text-gray-500">Approver</dt>
					<dd className="text-gray-900">{item.approver}</dd>
					<dt className="text-gray-500">Partner</dt>
					<dd className="text-gray-900">{item.partner}</dd>
				</dl>
				<h4 className="mb-1 mt-5 text-sm font-semibold">Case chain</h4>
				<ol className="mt-2">
					{item.chain.map((step) => (
						<li
							key={step.label}
							className={`relative flex items-start gap-3 pb-4 ${chainLine}`}
						>
							<span
								className={`relative z-[1] mt-[5px] h-3 w-3 flex-none rounded-full border-2 ${DOT[step.kind ?? "none"]}`}
							/>
							<div className="min-w-0 flex-1">
								<div className="text-sm font-medium text-gray-900">
									{step.label}
								</div>
								{step.detail && (
									<div className="mt-0.5 text-xs text-gray-500 [overflow-wrap:anywhere]">
										{step.detail}
									</div>
								)}
							</div>
							<span className="whitespace-nowrap pt-0.5 text-xs text-gray-500">
								{step.when}
							</span>
						</li>
					))}
				</ol>
				<div className="mt-5 rounded-xl border border-gray-200 bg-white px-4 py-3.5">
					<div className="flex flex-wrap items-center justify-between gap-2 text-sm font-semibold">
						<span>Proposal preview</span>
						<span
							className={`${pill} ${
								item.status === "approved"
									? STATUS.approved.style
									: STATUS.sent_back.style
							}`}
						>
							{PREVIEW_STATE[item.status]}
						</span>
					</div>
					<div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
						{fees.map(([label, value]) => (
							<div key={label}>
								<div className="text-[11px] text-gray-500">{label}</div>
								<div className="mt-0.5 text-base font-semibold">{value}</div>
							</div>
						))}
						<div>
							<div className="text-[11px] text-gray-500">Merchant saves</div>
							<div className="mt-0.5 text-base font-semibold text-[#157a3e]">
								{money(saved)} / mo
							</div>
							<div className="text-[11px] text-gray-500">
								{money(saved * 12)} a year
							</div>
						</div>
					</div>
					<p className="mt-3 text-xs text-gray-500">
						Merchant numbers only. No profit or margin appears in the merchant
						output.
					</p>
				</div>
				<details className="mt-4 text-[13px]">
					<summary className="cursor-pointer py-1.5 font-medium text-gray-700">
						Email to the approver
					</summary>
					<Email item={item} />
				</details>
			</div>
			{item.status === "pending" && (
				<div className="border-t border-gray-200 bg-white px-5 py-3.5">
					<label
						htmlFor="apNote"
						className="mb-2 block text-sm font-medium text-gray-700"
					>
						Note to the requester
					</label>
					<textarea
						id="apNote"
						rows={2}
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder="Optional. Goes in the email and the audit log."
						className={`mb-2.5 block w-full resize-y rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm leading-5 text-gray-900 ${focusRing}`}
					/>
					<div className="flex flex-wrap justify-end gap-2">
						<button
							type="button"
							className={`${actionBtn} border-gray-300 bg-white text-red-700 hover:bg-red-50`}
							onClick={() => onDecide("rejected", note)}
						>
							Reject
						</button>
						<button
							type="button"
							className={`${actionBtn} border-gray-300 bg-white text-gray-900 hover:bg-gray-100`}
							onClick={() => onDecide("sent_back", note)}
						>
							Send back
						</button>
						<button
							type="button"
							className={`${actionBtn} border-transparent bg-primary-500 text-white hover:bg-primary-600`}
							onClick={() => onDecide("approved", note)}
						>
							Approve
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

const SORTS: Record<Sort, (x: Approval, y: Approval) => number> = {
	oldest: (x, y) => y.ageHours - x.ageHours,
	newest: (x, y) => x.ageHours - y.ageHours,
	impact: (x, y) => savings(y) - savings(x),
};

// v9 table.compact.ap-table; narrow screens hide the same columns v9 hides.
const th =
	"whitespace-nowrap border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-left text-xs font-medium uppercase tracking-[.05em] text-gray-500";
const td = "whitespace-nowrap px-4 py-2.5 align-middle";
const hideSm = "max-sm:hidden";
const hideMd = "max-[1100px]:hidden";

const ApprovalsPage: React.FC = () => {
	// Real requests once the tenant has any; the sample queue (flag `approvals`) until then.
	const { data } = useListPricingApprovalsQuery();
	const [decide] = useDecidePricingApprovalMutation();
	const [decideError, setDecideError] = useState<string | null>(null);
	const live = useMemo(
		() => (data?.data.length ? data.data.map((r) => toApproval(r)) : null),
		[data],
	);
	const [demoItems, setDemoItems] = useState<Approval[]>(APPROVALS);
	const items = live ?? demoItems;
	const requesters = useMemo(
		() => [...new Set(items.map((a) => a.requestedBy))].sort(),
		[items],
	);
	const [tab, setTab] = useState<Tab>("pending");
	const [sort, setSort] = useState<Sort>("oldest");
	const [search, setSearch] = useState("");
	const [type, setType] = useState("");
	const [requester, setRequester] = useState("");
	const [belowFloorOnly, setBelowFloorOnly] = useState(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);

	const rows = useMemo(() => {
		const q = search.trim().toLowerCase();
		return items
			.filter(
				(a) =>
					(tab === "all" || a.status === tab) &&
					(!type || a.type === type) &&
					(!requester || a.requestedBy === requester) &&
					(!belowFloorOnly || a.belowFloor) &&
					(!q ||
						`${a.merchant} ${a.template} ${a.id} ${a.requestedBy}`
							.toLowerCase()
							.includes(q)),
			)
			.sort(SORTS[sort]);
	}, [items, tab, sort, search, type, requester, belowFloorOnly]);

	const selected = items.find((a) => a.id === selectedId);
	const closePane = useCallback(() => setSelectedId(null), []);
	const clearFilters = () => {
		setSearch("");
		setType("");
		setRequester("");
		setBelowFloorOnly(false);
		setTab("pending");
	};

	return (
		<div className="mx-auto w-full max-w-[1440px] px-8 pb-16">
			<div className="flex items-center justify-between gap-6 py-6">
				<div>
					<div className="text-xl font-normal leading-[1.5] text-gray-900">
						Approval queue
					</div>
					<div className="mt-1 max-w-[72ch] text-base leading-[1.5] text-gray-500">
						Quotes and discount requests that went outside their limits. Click
						a row to review it here.
					</div>
				</div>
				<div className="flex flex-shrink-0 items-center gap-3">
					<select
						aria-label="Sort"
						className={`w-40 rounded-lg border border-gray-300 bg-white py-2 pl-4 pr-10 text-sm leading-5 text-gray-900 ${focusRing}`}
						value={sort}
						onChange={(e) => setSort(e.target.value as Sort)}
					>
						<option value="oldest">Oldest first</option>
						<option value="newest">Newest first</option>
						<option value="impact">Largest impact</option>
					</select>
					<button
						type="button"
						className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium leading-5 text-primary-700 transition-colors hover:bg-gray-50"
						onClick={() => exportCsv(rows)}
					>
						<Download className="h-4 w-4 flex-none" />
						Export
					</button>
				</div>
			</div>
			{decideError && (
				<div
					role="alert"
					className="mb-3 rounded-lg border border-red-100 bg-red-50 px-4 py-2 text-sm text-red-700"
				>
					{decideError}
				</div>
			)}
			<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
				<div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3 sm:px-6 sm:py-4 min-[901px]:flex-nowrap">
					<div className="relative w-full flex-none sm:w-[260px]">
						<Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
						<input
							type="text"
							aria-label="Search merchant or rep"
							placeholder="Search merchant or rep"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className={`block h-9 w-full rounded-lg border border-gray-200 bg-gray-50 py-1 pl-9 pr-2.5 text-[13px] text-gray-900 ${focusRing}`}
						/>
					</div>
					<div
						role="tablist"
						aria-label="Status"
						className="inline-flex flex-none gap-0.5 rounded-[10px] border border-gray-200 bg-gray-100 p-[3px]"
					>
						{TABS.map((t) => (
							<button
								key={t.id}
								type="button"
								role="tab"
								aria-selected={tab === t.id}
								onClick={() => setTab(t.id)}
								className={`h-7 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium ${
									tab === t.id
										? "bg-white text-gray-900 shadow-[0_1px_2px_rgba(7,26,64,.12)]"
										: "text-gray-600 hover:text-gray-900"
								}`}
							>
								{t.label}
							</button>
						))}
					</div>
					<select
						aria-label="Type"
						className={filterSelect}
						value={type}
						onChange={(e) => setType(e.target.value)}
					>
						<option value="">All types</option>
						<option value="Quote">Quote</option>
						<option value="Discount request">Discount request</option>
					</select>
					<select
						aria-label="Requested by"
						className={filterSelect}
						value={requester}
						onChange={(e) => setRequester(e.target.value)}
					>
						<option value="">Requested by: anyone</option>
						{requesters.map((r) => (
							<option key={r} value={r}>
								{r}
							</option>
						))}
					</select>
					<label className="inline-flex h-9 flex-none cursor-pointer select-none items-center gap-2 whitespace-nowrap rounded-md border border-gray-300 bg-white px-2.5 text-[13px] text-gray-700">
						<input
							type="checkbox"
							className="peer sr-only"
							checked={belowFloorOnly}
							onChange={(e) => setBelowFloorOnly(e.target.checked)}
						/>
						<i className="relative h-4 w-7 rounded-full bg-gray-300 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-3 after:w-3 after:rounded-full after:bg-white after:transition-all after:content-[''] peer-checked:bg-[#0554f2] peer-checked:after:left-3.5 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#0554f2]" />
						Below floor only
					</label>
					<button
						type="button"
						className="whitespace-nowrap text-sm font-medium text-primary-700"
						onClick={clearFilters}
					>
						Clear filters
					</button>
				</div>
				<div className="overflow-x-auto">
					<table className="w-full border-collapse">
						<thead>
							<tr>
								<th className={th}>Merchant</th>
								<th className={`${th} ${hideSm}`}>Type</th>
								<th className={`${th} ${hideSm}`}>Requested by</th>
								<th className={`${th} text-right ${hideSm}`}>Proposed</th>
								<th className={`${th} text-right ${hideMd}`}>Floor</th>
								<th className={`${th} text-right`}>Vs floor</th>
								<th className={`${th} ${hideMd}`}>Age</th>
								<th className={th}>Status</th>
							</tr>
						</thead>
						<tbody className="text-sm text-gray-900">
							{rows.map((a) => (
								<tr
									key={a.id}
									tabIndex={0}
									role="button"
									aria-label={`Review ${a.merchant}`}
									onClick={() => setSelectedId(a.id)}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault();
											setSelectedId(a.id);
										}
									}}
									className={`cursor-pointer border-b border-gray-200 last:border-b-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#0554f2] ${
										a.id === selectedId
											? "bg-[#eaf0fe] shadow-[inset_3px_0_0_#0554f2]"
											: "hover:bg-gray-50"
									}`}
								>
									<td className={`${td} min-w-[220px] whitespace-normal`}>
										<div className="font-medium text-gray-900">
											{a.merchant}
										</div>
										<div className="text-xs text-gray-500">
											{a.id} · {a.template} · {a.partner}
										</div>
									</td>
									<td className={`${td} ${hideSm}`}>{a.type}</td>
									<td className={`${td} ${hideSm}`}>{a.requestedBy}</td>
									<td className={`${td} text-right ${hideSm}`}>
										{a.proposed ?? (
											<span className="text-gray-500">restricted</span>
										)}
									</td>
									<td className={`${td} text-right ${hideMd}`}>
										{a.floor ?? (
											<span className="text-gray-500">restricted</span>
										)}
									</td>
									<td
										className={`${td} text-right ${
											a.belowFloor && a.vsFloor ? "font-medium text-red-700" : ""
										}`}
									>
										{a.vsFloor ?? "—"}
										{a.note && (
											<div className="whitespace-normal text-xs font-normal text-gray-500">
												{a.note}
											</div>
										)}
									</td>
									<td className={`${td} text-gray-500 ${hideMd}`}>
										{age(a.ageHours)}
									</td>
									<td className={td}>
										<StatusBadge status={a.status} />
									</td>
								</tr>
							))}
							{rows.length === 0 && (
								<tr>
									<td colSpan={8} className="p-8 text-center text-gray-500">
										No requests match. Clear the filters to see every request.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
				<div className="flex items-center justify-between border-t border-gray-200 px-6 py-3 text-xs text-gray-500">
					{/* shortcut: no pager; v9 pages at 12 rows and the demo has 7 */}
					<span>
						{rows.length
							? `Showing 1–${rows.length} of ${rows.length}`
							: "Nothing matches"}
					</span>
				</div>
			</div>
			{selected && (
				<ReviewPane
					key={selected.id}
					item={selected}
					onClose={closePane}
					onDecide={(status, decisionNote) => {
						setDecideError(null);
						if (!selected.requestId) {
							setDemoItems((all) =>
								all.map((a) =>
									a.id === selected.id ? { ...a, status, decisionNote } : a,
								),
							);
							return;
						}
						if (status === "pending") return;
						decide({
							id: selected.requestId,
							status,
							...(decisionNote.trim() && { note: decisionNote.trim() }),
						})
							.unwrap()
							.catch((err) =>
								setDecideError(
									err?.data?.message ?? "Could not save the decision.",
								),
							);
					}}
				/>
			)}
		</div>
	);
};

export default ApprovalsPage;
